import { CrossSectionCanvas } from '@pages/cross-section/shared/CrossSectionCanvas';
import Konva from 'konva';


function drawModel(opts: {
    model: any,
    csCanvas: CrossSectionCanvas,
    hiddenLineIds: Array<string | number>,
    onClickCallback: (pipe, x, y) => void,
    type: 'model' | 'terrainModel'
}) {
    const { model, csCanvas, onClickCallback, hiddenLineIds } = opts;
    const center = csCanvas.getCenter();
    const group = new Konva.Group({
        id: 'model_' + model.id
    });

    const canvasOptions = csCanvas.canvasOptions;
    const lowestPoint = csCanvas.lowestPoint;

    model.lines.forEach(modelLine => {
        if (hiddenLineIds.includes(modelLine.id)) {
            return;
        }
        // These props are usually empty. Hide them unless some of them has some reasonable value
        const name = modelLine.name ?? '';
        const surfaceCode = (typeof (modelLine.surfaceCode) !== "undefined" && modelLine.surfaceCode !== null) ? modelLine.surfaceCode : "";
        const terrainCode = (typeof (modelLine.terrainCode) !== "undefined" && modelLine.terrainCode !== null) ? modelLine.terrainCode : "";
        let props;
        if (name.length > 0 || terrainCode.length > 0 || surfaceCode.length > 0) {
            props = { name: name, terrainCode: terrainCode, surfaceCode: surfaceCode };
        }

        modelLine.vectors.forEach(vector => {
            if (vector.points.length < 2) {
                return true;
            }

            const x0 = center + (vector.points[0].x * canvasOptions.x_scale);
            const y0 = (csCanvas.height - canvasOptions.bottom_margin) + lowestPoint * canvasOptions.y_scale - (vector.points[0].y * canvasOptions.y_scale) + csCanvas.panY;
            const x1 = center + (vector.points[1].x * canvasOptions.x_scale);
            const y1 = (csCanvas.height - canvasOptions.bottom_margin) + lowestPoint * canvasOptions.y_scale - (vector.points[1].y * canvasOptions.y_scale) + csCanvas.panY;

            if (x0 < canvasOptions.side_margin / 2 && x1 < canvasOptions.side_margin / 2) {
                return;
            } else if (x0 > csCanvas.width - canvasOptions.side_margin / 2 && x1 > csCanvas.width - canvasOptions.side_margin / 2) {
                return;
            } else if (y0 < canvasOptions.top_margin && y1 < canvasOptions.top_margin) {
                return;
            } else if (y0 > csCanvas.height - canvasOptions.bottom_margin && y1 > csCanvas.height - canvasOptions.bottom_margin) {
                return;
            }

            const xLen = (x1 - x0);
            const yLen = (y1 - y0);
            const lineLen = Math.sqrt(xLen * xLen + yLen * yLen);
            if (lineLen < 2) {
                return;
            }

            const line = new Konva.Line({
                name: 'model_' + model.modelId,
                points: [x0, y0, x1, y1],
                stroke: getRgb(model.color),
                strokeWidth: model.strokeWidth,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false
            });

            // Set tooltip line a bit shorter than the real line -> from +10% start to -10% from end
            if (lineLen > 10) {
                const corx = xLen / 10;
                const cory = yLen / 10;

                const tooltipLine = new Konva.Line({
                    name: 'tooltipline_model',
                    points: [x0 + corx, y0 + cory, x1 - corx, y1 - cory],
                    stroke: getRgb(model.color),
                    strokeWidth: 4,
                    lineCap: 'round',
                    lineJoin: 'round',
                    listening: true,
                    opacity: 0
                });
                tooltipLine.on('click tap', function () {
                    line.strokeWidth(line.strokeWidth() + 2);
                    showLineTooltip(opts.model.name, name, x0, y0, csCanvas, true);
                });
                tooltipLine.on('mouseover', function () {
                    line.strokeWidth(line.strokeWidth() + 2);
                    showLineTooltip(opts.model.name, name, x0, y0, csCanvas, false);
                });
                tooltipLine.on('mouseout', function () {
                    line.strokeWidth(line.strokeWidth() - 2);
                    csCanvas.clearHoverTooltips();
                    csCanvas.contentLayer.draw();
                });
                group.add(tooltipLine);
            }
            const vertex0Hitbox = new Konva.Circle({
                name: 'model_' + model.modelId,
                x: x0,
                y: y0,
                stroke: 'transparent',
                radius: 5
            });
            const vertex1Hitbox = new Konva.Circle({
                name: 'model_' + model.modelId,
                x: x1,
                y: y1,
                stroke: 'transparent',
                radius: 5
            });

            if (opts.type === "terrainModel") {
                line.dash([9, 9]);
            }
            vertex0Hitbox.on('click tap', function () {
                onClickCallback(vector.points[0], x0, y0);
            });
            vertex1Hitbox.on('click tap', function () {
                onClickCallback(vector.points[1], x1, y1);
            });

            vertex0Hitbox.on('mouseover', function () {
                showVertexTooltip(vector.points[0], x0, y0, model.name, modelLine.name, props, csCanvas);
                csCanvas.drawSnapCursor(true, x0, y0);
            });
            vertex1Hitbox.on('mouseover', function () {
                showVertexTooltip(vector.points[1], x1, y1, model.name, modelLine.name,props, csCanvas);
                csCanvas.drawSnapCursor(true, x1, y1);
            });

            vertex0Hitbox.on('mouseout', csCanvas.clearHoverTooltips);
            vertex1Hitbox.on('mouseout', csCanvas.clearHoverTooltips);
            group.add(vertex0Hitbox);
            group.add(vertex1Hitbox);
            group.add(line);
        });
    });

    model.points.forEach(modelPoint => {

        const x = csCanvas.metersToScreenCoordsX(modelPoint.point.x);
        const y = csCanvas.metersToScreenCoordsY(modelPoint.point.y);

        if (x < canvasOptions.side_margin / 2 || x > csCanvas.width - canvasOptions.side_margin / 2) {
            return;
        }
        if (y < canvasOptions.top_margin || y > csCanvas.height - canvasOptions.bottom_margin) {
            return;
        }

        let color = model.color;
        if (!color) {
            color = "0,0,0";
        }

//            if (modelPoint.props && "color" in modelPoint.props) {
//                color = modelPoint.props["color"];
//            }

        const circle = new Konva.Circle({
            name: 'model_' + model.modelId,
            x: x,
            y: y,
            stroke: getRgb(color),
            fill: getRgb(color),
            radius: 2,
            listening: false
        });

        const hitbox = new Konva.Circle({
            name: 'model_' + model.modelId,
            x: x,
            y: y,
            stroke: 'transparent',
            radius: 5
        });

        hitbox.on('click tap', function () {
            onClickCallback(modelPoint.point, x, y);
            showVertexTooltip(modelPoint.point, x, y, opts.model.name, modelPoint.name, modelPoint.props, csCanvas, true);
        });

        hitbox.on('mouseover', function () {
            showVertexTooltip(modelPoint.point, x, y, opts.model.name, modelPoint.name, modelPoint.props, csCanvas, false);
        });

        hitbox.on('mouseout', csCanvas.clearHoverTooltips);

        group.add(hitbox);
        group.add(circle);
    });

    csCanvas.contentLayer.add(group);
}

function showLineTooltip(modelName: string, lineName: string, x: number, y: number, csCanvas: CrossSectionCanvas, sticky = false) {
    const text = [modelName, lineName].filter(Boolean).join("\n");
    csCanvas.drawTooltipAtMousePosition(text, -25, sticky);
}

function showVertexTooltip(point: any, x: number, y: number, modelName: string, lineName: string, props: Record<string, any>, csCanvas: CrossSectionCanvas, sticky = false) {
    const text = [
        modelName,
        lineName,
        kuura.getMessage('js.cross_section.b') + ': ' + (point.x).toFixed(2),
        kuura.getMessage('js.cross_section.z') + ': ' + (point.y).toFixed(2)
    ].filter(Boolean);

    if (props) {
        if (Object.keys(props).length > 0) {
            text.push("\n");
        }
        Object.entries(props).forEach(([key, val]) => {
            text.push(key + ": " + val);
        });
    }

    csCanvas.tooltipLayer.add(new Konva.Rect({
        x: x - 4,
        y: y - 4,
        width: 9,
        height: 9,
        stroke: 'gray',
        strokeWidth: 1,
        listening: false
    }));
    csCanvas.drawTooltip(x, y - 15, text.join("\n"), sticky);
}

function drawBorder(border: any, csCanvas: CrossSectionCanvas) {
    const point = border.point;
    const center = csCanvas.getCenter();
    const canvasOptions = csCanvas.canvasOptions;
    const x = center + (point.x * canvasOptions.x_scale);
    const y0 = canvasOptions.top_margin;
    const y1 = csCanvas.height - canvasOptions.bottom_margin;

    const line = new Konva.Line({
        name: 'model_' + point.id,
        points: [x, y0, x, y1],
        stroke: getRgb(border.color),
        strokeWidth: 1,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false
    });

    csCanvas.contentLayer.add(line);
}

const getRgb = (color: string) => 'rgb(' + color + ')'

export const CrossSectionModel = {
    drawModel,
    drawBorder
}
